import React, { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import type { EyebrowTextTransform } from "../../domain/types";
import { useStableDomId, useStableScopedDomId } from "../../useStableDomId";

const CASE_OPTIONS = [
  { value: "none", label: "Normal", useTextLabel: true },
  { value: "uppercase", label: "Uppercase", useTextLabel: false },
  { value: "lowercase", label: "Lowercase", useTextLabel: false },
  { value: "capitalize", label: "Title", useTextLabel: false },
] as const satisfies ReadonlyArray<{
  value: EyebrowTextTransform;
  label: string;
  useTextLabel: boolean;
}>;

function TextCaseGlyph({ value }: { value: EyebrowTextTransform }) {
  if (value === "uppercase") {
    return <span className="email-builder-settings-case-glyph">AA</span>;
  }

  if (value === "lowercase") {
    return <span className="email-builder-settings-case-glyph">aa</span>;
  }

  return (
    <span className="email-builder-settings-case-glyph email-builder-settings-case-glyph--title">
      <span className="email-builder-settings-case-glyph__cap">A</span>a
    </span>
  );
}

type TextCaseOptionButtonProps = {
  selected: boolean;
  tooltip: string;
  ariaLabel: string;
  onClick: () => void;
  children: ReactNode;
};

function TextCaseOptionButton({
  selected,
  tooltip,
  ariaLabel,
  onClick,
  children,
}: TextCaseOptionButtonProps) {
  const tooltipId = useStableScopedDomId("email-builder-text-case-option", ariaLabel);

  return (
    <OverlayTrigger
      placement="bottom"
      delay={{ show: 400, hide: 100 }}
      overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}
    >
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        aria-label={ariaLabel}
        data-on={selected ? "true" : "false"}
        onClick={onClick}
      >
        {children}
      </button>
    </OverlayTrigger>
  );
}

export function TextCaseControl({
  value,
  onChange,
}: {
  value: EyebrowTextTransform;
  onChange: (value: EyebrowTextTransform) => void;
}) {
  const { t } = useTranslation();
  const labelId = useStableDomId("email-builder-text-case-label");

  return (
    <div className="email-builder-settings-inline-field">
      <div id={labelId} className="email-builder-settings-inline-field__label">
        {t("Case")}
      </div>
      <div className="email-builder-settings-inline-field__control">
        <div
          className="email-builder-settings-segment-wide email-builder-settings-segment-wide--with-icons"
          role="radiogroup"
          aria-labelledby={labelId}
        >
          {CASE_OPTIONS.map(option => {
            const optionLabel = t(option.label);
            const selected = value === option.value;

            return (
              <TextCaseOptionButton
                key={option.value}
                selected={selected}
                tooltip={optionLabel}
                ariaLabel={optionLabel}
                onClick={() => onChange(option.value)}
              >
                {option.useTextLabel ? optionLabel : <TextCaseGlyph value={option.value} />}
              </TextCaseOptionButton>
            );
          })}
        </div>
      </div>
    </div>
  );
}
